/**
 * Frontend interface for interacting with the API.
 */
import { getUserToken } from './user';
import type { Location } from '../types/Location';
import type { Order, OrderItem } from '../types/Order';
import type { Detection } from '../types/Detection';
import type { Average } from '../types/Average';
import type { RankPayload } from '../types/Rank';
import type { DayPartData } from '../types/DayPartData';
import type { RegionsStats } from '../types/RegionsStats';
import type { Camera } from '../types/Camera';
import { dateToUtc } from './time';
import { type FranchiseUser } from '../types/FranchiseUser';
import { type FranchiseLocation } from '../types/FranchiseLocation';
import { type FranchiseConfiguration } from '../types/FranchiseConfiguration';
import { type Stack } from '@/types/Stack';
import { type GroupResponse } from '@/types/GroupResponse';
import { type HoursOfOperation } from '@/types/HoursOfOperation';
import { type UserPreference, type UserPreferenceRequest } from '@/types/UserPreference';
import { type ReportConfig } from '@/types/ReportConfig';
import { type UserProfileV3 } from '@/types/UserProfileV3';
import { type FranchiseV3 } from '@/types/Franchise';
import { type ReportScheduleConfig } from '@/types/ReportScheduleConfig';
import { type LocationStatus } from '@/types/LocationStatus';
import { type StatusRequestObject } from '@/components/settings/account/types';

export async function getAllLabelmeImages(
  franchiseId: number,
  locationId: number
): Promise<object> {
  const urls = await getLabelmeImages(franchiseId, locationId);
  const results = await processImageUrls(urls);

  return results;
}

export async function getMothershipLogs(
  franchiseId: string,
  locations: string[]
): Promise<any> {
  const token = await getUserToken();
  if (!token) {
    console.error('No token found.');
    return null;
  }

  try {
    // Create the query params.
    const queryParams = new URLSearchParams();

    // Append the location IDs to the query params.
    locations.forEach((locationId) => {
      queryParams.append('locationId', locationId);
    });

    // Add default value for amountLines
    queryParams.append('amountLines', '2000'); // Default to 2000 or any value expected by the API

    // Make the request to the API with query parameters.
    const response = await fetch(
      `${
        import.meta.env.VITE_API_URL
      }/franchises/${franchiseId}/locations/logs?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
      }
    );

    console.log('Response:', response); // Print the entire response object

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to fetch mothership logs:', errorText);
      return null;
    }

    // Since the body is expected to be JSON, parse it
    const data = await response.json();
    console.log('Response Body:', data); // Print the parsed body

    return data.latestLogs; // Return the locationLogs map
  } catch (error) {
    console.error('Error fetching mothership logss:', error);
    return null;
  }
}

export async function getMothershipMetrics(
  franchiseId: string,
  locations: string[]
): Promise<any> {
  const token = await getUserToken();
  if (!token) {
    console.error('No token found.');
    return null;
  }

  try {
    // Create the query params.
    const queryParams = new URLSearchParams();

    // Append the location IDs to the query params.
    locations.forEach((locationId) => {
      queryParams.append('locationId', locationId);
    });

    // Make the request to the API with query parameters.
    const response = await fetch(
      `${
        import.meta.env.VITE_API_URL
      }/franchises/${franchiseId}/locations/metrics?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
      }
    );

    console.log('Response:', response); // Print the entire response object

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to fetch mothership metrics:', errorText);
      return null;
    }

    // Since the body is expected to be JSON, parse it
    const data = await response.json();
    console.log('Response Body:', data); // Print the parsed body

    return data.locationMetrics; // Return the locationMetrics map
  } catch (error) {
    console.error('Error fetching mothership metrics:', error);
    return null;
  }
}

// export async function getMothershipMetrics(franchiseId: string, locations: string[]): Promise<any> {
//   const token = await getUserToken();
//   if (!token) {
//     console.error('No token found.');
//     return null;
//   }
//   console.log('b')

//   try {
//     const response = await fetch(
//       `${import.meta.env.VITE_API_URL}/franchises/${franchiseId}/locations/metrics`,
//       {
//         method: 'GET',
//         headers: {
//           Authorization: token,
//         },
//       }
//     );
//     console.log('c')

//     if (!response.ok) {
//       const errorText = await response.text();
//       console.error('Failed to fetch mothership metrics:', errorText);
//       return null;
//     }

//     const data = await response.json();
//     const locationMetrics = JSON.parse(data.body).locationMetrics;
//     return locationMetrics; // Return the locationMetrics map

//   } catch (error) {
    // console.error('Error fetching mothership metrics:', error);
//     return null;
//   }
// }

// export async function getMothershipLogs(franchiseId: string): Promise<any> {
//   const token = await getUserToken();
//   if (!token) {
//     console.error('No token found.');
//     return null;
//   }

//   try {
//     const response = await fetch(
//       `${import.meta.env.VITE_API_URL}/franchises/${franchiseId}/locations/logs`,
//       {
//         method: 'GET',
//         headers: {
//           Authorization: token,
//         },
//       }
//     );

//     if (!response.ok) {
//       const errorText = await response.text();
//       console.error('Failed to fetch mothership logs:', errorText);
//       return null;
//     }

//     const data = await response.json();
//     const locationLogs = JSON.parse(data.body).locationLogs;
//     return locationLogs; // Return the locationLogs map

//   } catch (error) {
//     console.error('Error fetching mothership logs:', error);
//     return null;
//   }
// }

export async function callMothership(
  machine_numbers: string,
  action: string
): Promise<boolean> {
  const token = await getUserToken();

  if (!token) {
    console.error('No token found.');
    return false;
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/mothership/run_playbook`,
      {
        method: 'POST',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          machine_numbers,
          action,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text(); // Read the error message
      console.error('Failed to post mothership command:', errorText);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error calling mothership:', error);
    return false;
  }
}

export async function callMothershipDotenv(
  machine_numbers: string,
  variable: string,
  dotenv_action: string,
  value: string
): Promise<boolean> {
  const token = await getUserToken();
  console.log('cmd', machine_numbers, variable, dotenv_action, value);

  if (!token) {
    console.error('No token found.');
    return false;
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/mothership/run_configure`,
      {
        method: 'POST',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          machine_numbers, // Pass machine numbers
          variable, // Pass the variable
          dotenv_action, // Pass dotenv action (e.g., ADD, REMOVE)
          value, // Pass the value (e.g., PASSTHROUGH)
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text(); // Read the error message
      console.error('Failed to post mothership command:', errorText);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error calling mothership:', error);
    return false;
  }
}

async function processImageUrls(
  imageMap: Map<string, string>
): Promise<object> {
  const results = {};

  for (const [key, url] of imageMap.entries()) {
    try {
      const imageData = await fetchImageData(url);
      results[key] = imageData;
    } catch (error) {
      console.error(`Error processing URL ${url}:`, error);
    }
  }

  return results;
}

async function fetchImageData(url: string): Promise<string> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(
        `Failed to fetch image data: ${response.status} ${response.statusText}`
      );
    }

    const blob = await response.blob();
    return await convertBlobToBase64(blob);
  } catch (error) {
    console.error(`Error fetching image data from ${url}:`, error);
    throw error;
  }
}

async function convertBlobToBase64(blob: Blob): Promise<string> {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result as string);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export async function getLabelmeImages(
  franchiseId: number,
  locationId: number
): Promise<Map<string, string>> {
  const token = await getUserToken();

  if (!token) {
    console.error('No token found.');
    return new Map();
  }

  try {
    const response = await fetch(
      `${
        import.meta.env.VITE_API_URL
      }/franchises/${franchiseId}/locations/${locationId}/regions`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
      }
    );
    console.log('Response details:', response);

    if (!response.ok) {
      console.error(
        'Failed to fetch images:',
        response.status,
        response.statusText
      );
      return new Map();
    }

    const data = await response.json();
    console.log('Parsed JSON data:', data);

    return new Map(Object.entries(data));
  } catch (error) {
    console.error('Error fetching images:', error);
    return new Map();
  }
}

export async function postLabelmeImages(
  franchiseId: number,
  locationId: number,
  jsImage: string,
  fileName: string
): Promise<boolean> {
  const token = await getUserToken();

  if (!token) {
    console.error('No token found.');
    return false;
  }

  try {
    const response = await fetch(
      `${
        import.meta.env.VITE_API_URL
      }/franchises/${franchiseId}/locations/${locationId}/regions`,
      {
        method: 'POST',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileType: 'application/json',
          fileKey: fileName,
          fileContent: jsImage,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text(); // Read the error message
      console.error('Failed to post images:', errorText);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error fetching images:', error);
    return false;
  }
}

export async function deleteLocation(
  franchiseId: string,
  locationId: string
): Promise<void> {
  const token = await getUserToken();

  if (!token) {
    console.error('No token found.');
    return;
  }

  await fetch(
    `${import.meta.env.VITE_API_URL}/franchises/${franchiseId}/locations`,
    {
      method: 'DELETE',
      headers: {
        Authorization: token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        locationId,
      }),
    }
  );
}

/**
 * @returns The list of franchise summaries (id, displayName, role, configuration)
 */
export async function getUserFranchisesV3(): Promise<FranchiseV3[]> {
  const token = await getUserToken();
  if (!token) {
    console.error('No token found.');
    throw new Error('No token found.');
  }

  const res = await fetch(
    `${import.meta.env.VITE_API_URL}/v3/user/franchises`,
    {
      method: 'GET',
      headers: {
        Authorization: token,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Error fetching user franchises v3: ${res.status} — ${text}`);
  }

  const raw = (await res.json()) as Array<{
    franchiseId: string;
    displayName: string;
    userRole: FranchiseV3['role'];
    configuration: FranchiseV3['configuration'];
  }>;

  return raw.map(r => ({
    id: r.franchiseId,
    displayName: r.displayName,
    role: r.userRole,
    configuration: r.configuration
  }));
}

/**
 *
 * @returns The user profile V3.
 */
export async function getUserProfileV3(franchiseId: string): Promise<UserProfileV3> {
  const token = await getUserToken();

  if (token === null) {
    console.error('No token found.');
    throw new Error('No token found.');
  }
  // For mobile app FCM posting auth
  console.log(JSON.stringify({ CognitoUserToken: token }));

  // Create the query params.
  const queryParams = new URLSearchParams();
  queryParams.append('franchiseId', franchiseId);

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/v3/user/profile?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
      }
    );

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Error fetching user profile v3: ${response.status} — ${text}`);
    }

    const data = await response.json();

    // Convert each location's customPermissions from a JS object to a Map
    data.locations = data.locations.map((loc: Location) => ({
      ...loc,
      customPermissions: new Map<string, unknown>(
        Object.entries(loc.customPermissions ?? {})
      ),
    }));
    return data as UserProfileV3;
  } catch (error) {
    console.error(error);
    throw new Error('Unexpected error fetching user profile v3');
  }
}

/**
 * @description Get the orders for locations on a given day.
 */
export async function getLocationOrders(
  franchiseId: string,
  date: Date,
  locations: Location[]
): Promise<Order[]> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return [] as Order[];
  }

  date.setHours(0);
  date.setMinutes(0);
  date.setSeconds(0);

  const start = dateToUtc(date, locations[0].timezone);
  const end = dateToUtc(date, locations[0].timezone);
  end.setDate(end.getDate() + 1);

  try {
    // Create the query params.
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', start.toISOString());
    queryParams.append('endDate', end.toISOString());

    for (let i = 0; i < locations.length; i++) {
      queryParams.append('locationId', locations[i].id);
    }

    // Make the request.
    const response = await fetch(
      `${
        import.meta.env.VITE_API_URL
      }/franchises/${franchiseId}/orders?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
      }
    );

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    // Group orderItems by orderId in each Order
    const ordersWithItems = data.orders.map((order: Order) => ({
      ...order,
      items: data.orderItems.filter(
        (item: OrderItem) => item.orderId === order.orderId
      ),
    }));
    return ordersWithItems;
  } catch (error) {
    console.error(error);
  }

  return [];
}

/**
 * @description Get the order totals for a week at a location.
 */
export async function getOrderTotals(
  franchiseId: string,
  startDate: Date,
  endDate: Date,
  locations: string[],
  timezone: string
): Promise<
  Array<{
    date: string;
    total: number;
  }>
> {
  const token = await getUserToken();

  startDate.setHours(0);
  startDate.setMinutes(0);
  startDate.setSeconds(0);

  endDate.setHours(0);
  endDate.setMinutes(0);
  endDate.setSeconds(0);

  const start = dateToUtc(startDate, timezone);
  const end = dateToUtc(endDate, timezone);

  if (token === null) {
    console.log('No token found.');
    return [];
  }

  try {
    // Create the query params.
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', start.toISOString());
    queryParams.append('endDate', end.toISOString());

    for (let i = 0; i < locations.length; i++) {
      queryParams.append('locationId', locations[i]);
    }

    // Make the request.
    const response = await fetch(
      `${
        import.meta.env.VITE_API_URL
      }/franchises/${franchiseId}/orders/totals?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
      }
    );

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(error);
  }

  return [];
}

/**
 * @description Get the order totals for a week at a location.
 */
export async function getOrderItemTimes(
  franchiseId: string,
): Promise<
  Array<{
  description: string;
  count: number;
  orderTimeWithItem: number;
  itemTime: number;
  }>
> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return [];
  }

  try {
    // Create the query params.
    const queryParams = new URLSearchParams();
    // Make the request.
    const response = await fetch(
      `${
        import.meta.env.VITE_API_URL
      }/franchises/${franchiseId}/orders/itemTimes?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
      }
    );

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(error);
  }

  return [];
}

/**
 * @description Get the list of users for a given franchise
 */
export async function getFranchiseUsers(franchiseId: string): Promise<{
  userData: FranchiseUser[];
  franchiseLocations: FranchiseLocation[];
  franchiseConfiguration: FranchiseConfiguration | null;
}> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return {
      userData: [],
      franchiseLocations: [],
      franchiseConfiguration: null,
    };
  }

  try {
    // Make the request.
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/franchises/${franchiseId}/users`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
      }
    );

    if (!response.ok) {
      return {
        userData: [],
        franchiseLocations: [],
        franchiseConfiguration: null,
      };
    }

    const data = await response.json();

    // Convert each FranchiseUser's locationPermissions to a Map
    const userData: FranchiseUser[] = data.userData.map(
      (user: FranchiseUser) => {
        // Safely handle if locationPermissions is missing or null
        if (
          user.locationPermissions &&
          typeof user.locationPermissions === 'object'
        ) {
          return {
            ...user,
            locationPermissions: new Map(
              Object.entries(user.locationPermissions).map(
                ([locationId, permissions]) => [
                  locationId,
                  new Map(
                    Object.entries(permissions as Record<string, unknown>)
                  ),
                ]
              )
            ),
          };
        }

        // If there's no locationPermissions, return a user with an empty Map
        return {
          ...user,
          locationPermissions: new Map(),
        };
      }
    );

    return {
      userData,
      franchiseLocations: data.franchiseLocations,
      franchiseConfiguration: data.franchiseConfiguration,
    };
  } catch (error) {
    console.error(error);
  }

  return { userData: [], franchiseLocations: [], franchiseConfiguration: null };
}

/**
 * @description Get the list of users for a given franchise
 */
export async function updateFranchiseConfiguration(
  franchiseId: string,
  franchiseConfiguration: FranchiseConfiguration
): Promise<boolean> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return false;
  }

  try {
    // Make the request.
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/franchises/${franchiseId}/users`,
      {
        method: 'POST',
        headers: {
          Authorization: token,
        },
        body: JSON.stringify(franchiseConfiguration),
      }
    );

    if (!response.ok) {
      return false;
    }
    return true;
  } catch (error) {
    console.error(error);
  }

  return false;
}

/**
 * @description Get the detections for a location.
 */
export async function getDetections(
  startDate: Date,
  endDate: Date,
  locations: string[],
  detectionType: string,
  franchiseId?: string,
  options: { signal?: AbortSignal } = {}
): Promise<Detection[]> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return [] as Detection[];
  }

    // Create the query params.
    const queryParams = new URLSearchParams();
    queryParams.append(
      'startDate',
      `${startDate.getFullYear()}-${
        startDate.getMonth() + 1
      }-${startDate.getDate()}`
    );
    queryParams.append(
      'endDate',
      `${endDate.getFullYear()}-${endDate.getMonth() + 1}-${endDate.getDate()}`
    );
    queryParams.append('detectionType', detectionType);

    for (let i = 0; i < locations.length; i++) {
      queryParams.append('locationId', locations[i]);
    }

    if (franchiseId) {
      queryParams.append('franchiseId', franchiseId.toString());
    }

    // Make the request.
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/detections?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
        signal: options.signal, // Pass the abort signal here
      }
    );

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return data;
}

/**
 * @description Get the averages for a location.
 */
export async function getAverages(
  franchiseId: string,
  startDate: Date,
  endDate: Date,
  locations: string[],
  detectionType: string,
  interval: number,
  options: { signal?: AbortSignal } = {}
): Promise<Average[]> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return [] as Average[];
  }

  try {
    // Create the query params.
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', startDate.toISOString());
    queryParams.append('endDate', endDate.toISOString());
    queryParams.append('detectionType', detectionType);
    queryParams.append('interval', interval.toString());

    for (let i = 0; i < locations.length; i++) {
      queryParams.append('locationIds', locations[i]);
    }

    // Make the request.
    const response = await fetch(
      `${
        import.meta.env.VITE_API_URL
      }/v3/user/franchises/${franchiseId}/averages?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
        signal: options.signal, // Pass the abort signal here
      }
    );

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(error);
  }

  return [];
}

/**
 * @description Get the averages for a location.
 */
export async function getGroupsAverages(
  franchiseId: string,
  startDate: Date,
  endDate: Date,
  groupNames: string[],
  detectionType: string,
  options: { signal?: AbortSignal } = {}
): Promise<Average[]> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return [] as Average[];
  }

  try {
    // Create the query params.
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', startDate.toISOString());
    queryParams.append('endDate', endDate.toISOString());
    queryParams.append('detectionType', detectionType);
    queryParams.append('interval', '0');

    for (let i = 0; i < groupNames.length; i++) {
      queryParams.append('groupNames', groupNames[i]);
    }

    // Make the request.
    const response = await fetch(
      `${
        import.meta.env.VITE_API_URL
      }/v3/user/franchises/${franchiseId}/averages?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
        signal: options.signal, // Pass the abort signal here
      }
    );

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(error);
  }

  return [];
}

/**
 * @description Get the ranks for a location.
 */
export async function getRanks(
  franchiseId: string,
  startDate: Date,
  endDate: Date,
  locations: string[],
  detectionType: string,
  options: { signal?: AbortSignal } = {}
): Promise<RankPayload> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return {} as RankPayload;
  }

  try {
    // Create the query params.
    const queryParams = new URLSearchParams();
    queryParams.append(
      'startDate',
      `${startDate.getFullYear()}-${
        startDate.getMonth() + 1
      }-${startDate.getDate()}`
    );
    queryParams.append(
      'endDate',
      `${endDate.getFullYear()}-${endDate.getMonth() + 1}-${endDate.getDate()}`
    );
    queryParams.append('detectionType', detectionType);

    for (let i = 0; i < locations.length; i++) {
      queryParams.append('locationId', locations[i]);
    }

    // Make the request.
    const response = await fetch(
      `${
        import.meta.env.VITE_API_URL
      }/franchises/${franchiseId}/ranks?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
        signal: options.signal, // Pass the abort signal here
      }
    );

    if (!response.ok) {
      return {} as RankPayload;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(error);
  }

  return {} as RankPayload;
}

/**
 * @description Get the regions stats for a location.
 */
export async function getRegionsStats(
  franchiseId: string,
  startDate: Date,
  endDate: Date,
  locations: string[],
  detectionType: string,
  options: { signal?: AbortSignal } = {}
): Promise<RegionsStats[]> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return [] as RegionsStats[];
  }

  // Create the query params.
  const queryParams = new URLSearchParams();

  startDate.setHours(0);
  endDate.setHours(0);

  queryParams.append('startDate', startDate.toISOString().slice(0, 10));
  queryParams.append('endDate', endDate.toISOString().slice(0, 10));
  queryParams.append('detectionType', detectionType);

  for (let i = 0; i < locations.length; i++) {
    queryParams.append('locationIds', locations[i]);
  }

  // Make the request.
  const response = await fetch(
    `${
      import.meta.env.VITE_API_URL
    }/v3/user/franchises/${franchiseId}/regions?${queryParams.toString()}`,
    {
      method: 'GET',
      headers: {
        Authorization: token,
      },
      signal: options.signal, // Pass the abort signal here
    }
  );

  if (!response.ok) {
    return [];
  }

  const data = await response.json();
  return data;
}

/**
 * @description Get the regions stats for groups.
 */
export async function getGroupRegionsStats(
  franchiseId: string,
  startDate: Date,
  endDate: Date,
  groups: string[],
  detectionType: string,
  options: { signal?: AbortSignal } = {}
): Promise<RegionsStats[]> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return [] as RegionsStats[];
  }

  try {
    // Create the query params.
    const queryParams = new URLSearchParams();

    startDate.setHours(0);
    endDate.setHours(0);

    queryParams.append('startDate', startDate.toISOString().slice(0, 10));
    queryParams.append('endDate', endDate.toISOString().slice(0, 10));
    queryParams.append('detectionType', detectionType);

    for (let i = 0; i < groups.length; i++) {
      queryParams.append('groupNames', groups[i]);
    }

    // Make the request.
    const response = await fetch(
      `${
        import.meta.env.VITE_API_URL
      }/v3/user/franchises/${franchiseId}/regions?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
        signal: options.signal, // Pass the abort signal here
      }
    );

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(error);
  }

  return [];
}

/**
 * @description Get the stack data for a location.
 */
export async function getStackDataByLocationId(
  franchiseId: string,
  startDate: Date,
  endDate: Date,
  locations: string[],
  options: { signal?: AbortSignal } = {}
): Promise<Stack | undefined> {
  const token = await getUserToken();
  const results: Stack | undefined = undefined;

  if (token === null) {
    console.log('No token found.');
    return results; // Return undefined if no token
  }

    // Create the query params.
    const queryParams = new URLSearchParams();

    queryParams.append('start_date', startDate.toISOString().slice(0, 10));
    queryParams.append('end_date', endDate.toISOString().slice(0, 10));
    queryParams.append('timezone', 'America/Los_Angeles');
    queryParams.set('location_ids', locations.join(','));

    // Make the request.
    const response = await fetch(
      `${
        import.meta.env.VITE_API_URL
      }/franchises/${franchiseId}/stacks-driveoffs?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
        signal: options.signal, // Pass the abort signal here
      }
    );

    if (!response.ok) {
      console.error(
        `Failed to fetch: ${response.status} ${response.statusText}`
      );
      return results; // Return undefined if response is not ok
    }

    const data = await response.json();
    return data as Stack;
}

/**
 * @description Get day part data for a location.
 */
export async function getDayPartsData(
  franchiseId: string,
  startDate: Date,
  endDate: Date,
  locations: string[],
  detectionType: string,
  options: { signal?: AbortSignal } = {}
): Promise<DayPartData[]> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return [] as DayPartData[];
  }

    // Create the query params.
    const queryParams = new URLSearchParams();

    queryParams.append(
      'startDate',
      `${startDate.getFullYear()}-${
        startDate.getMonth() + 1
      }-${startDate.getDate()}`
    );
    queryParams.append(
      'endDate',
      `${endDate.getFullYear()}-${endDate.getMonth() + 1}-${endDate.getDate()}`
    );
    queryParams.append('detectionType', detectionType);

    for (let i = 0; i < locations.length; i++) {
      queryParams.append('locationIds', locations[i]);
    }

    // Make the request.
    const response = await fetch(
      `${
        import.meta.env.VITE_API_URL
      }/v3/user/franchises/${franchiseId}/day-parts?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
        signal: options.signal, // Pass the abort signal here
      }
    );

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return data;
}

/**
 * @description Get day part data for a location.
 */
export async function getGroupDayPartsData(
  franchiseId: string,
  startDate: Date,
  endDate: Date,
  groupNames: string[],
  detectionType: string,
  options: { signal?: AbortSignal } = {}
): Promise<DayPartData[]> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return [] as DayPartData[];
  }

  try {
    // Create the query params.
    const queryParams = new URLSearchParams();

    queryParams.append(
      'startDate',
      `${startDate.getFullYear()}-${
        startDate.getMonth() + 1
      }-${startDate.getDate()}`
    );
    queryParams.append(
      'endDate',
      `${endDate.getFullYear()}-${endDate.getMonth() + 1}-${endDate.getDate()}`
    );
    queryParams.append('detectionType', detectionType);

    for (let i = 0; i < groupNames.length; i++) {
      queryParams.append('groupNames', groupNames[i]);
    }

    // Make the request.
    const response = await fetch(
      `${
        import.meta.env.VITE_API_URL
      }/v3/user/franchises/${franchiseId}/day-parts?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
        signal: options.signal, // Pass the abort signal here
      }
    );

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(error);
  }

  return [];
}

/**
 * @description Get the cameras for a location.
 * @param location The location to get cameras for.
 * @returns The cameras for a location.
 */
export async function getLocationCameras(
  location: Location
): Promise<Camera[]> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return [] as Camera[];
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/franchises/${
        location.franchiseId
      }/locations/${location.id}/cameras`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
      }
    );

    if (!response.ok) {
      return [] as Camera[];
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(error);
  }

  return [] as Camera[];
}

/**
 * @param franchiseId The franchise id of the location
 * @param locationId The location id to update.
 * @param displayName The display name to update.
 * @param dayParts The day parts to update.
 * @returns True if the location settings were updated successfully.
 */
export async function putLocationSettings(
  franchiseId: string,
  locationId: string,
  displayName: string,
  openOrCloseData: HoursOfOperation
): Promise<boolean> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return false;
  }

  try {
    const response = await fetch(
      `${
        import.meta.env.VITE_API_URL
      }/franchises/${franchiseId}/locations/${locationId}/settings`,
      {
        method: 'PUT',
        headers: {
          Authorization: token,
        },
        body: JSON.stringify({
          displayName,
          openOrCloseData,
        }),
      }
    );

    if (!response.ok) {
      return false;
    }

    return true;
  } catch (error) {
    console.error(error);
  }

  return false;
}

/**
 * @param locationId The location ID to fetch the address for
 * @returns A string address if found, otherwise null
 */
export async function getLocationsWithAddress(
  locations: Location[]
): Promise<string | null> {
  const token = await getUserToken();

  if (token === null) {
    return null;
  }

  try {
    const queryParams = new URLSearchParams();
    for (let i = 0; i < locations.length; i++) {
      queryParams.append('locationIds', locations[i].id);
    }
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/addresses?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
      }
    );
    if (!response.ok) {
      console.log(response.status, response.statusText);
      return null;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(error);
    return null;
  }
}

/**
 *
 * @param notificationLocations - The notification locations to update.
 * @returns The updated user profile.
 */
export async function patchUserProfile(
  notificationLocations: string[]
): Promise<UserProfileV3> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    throw new Error('No token found.');
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/user/profile`,
      {
        method: 'PATCH',
        headers: {
          Authorization: token,
        },
        body: JSON.stringify({
          notificationLocations,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`Error updating user profile: ${response.status} — ${response.statusText}`);
    }
    const data = await response.json();
    return data as UserProfileV3;
  } catch (error) {
    console.error(error);
    throw new Error('Unexpected error updating user profile');
  }
}

/**
 *
 * @param notificationLocations - The notification locations to update.
 * @returns The updated user profile.
 */
export async function postUserProfile(
  newUser: FranchiseUser
): Promise<boolean> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return false;
  }

  try {
    const franchiseId = newUser.franchiseId;
    const role = newUser.userRole;
    const email = newUser.email;
    const locations = newUser.locationIds.map((locId) => {
      return { locationId: locId, franchiseId };
    });

    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/user/profile`,
      {
        method: 'POST',
        headers: {
          Authorization: token,
        },
        body: JSON.stringify({
          locations,
          franchiseId,
          role,
          email,
        }),
      }
    );

    if (!response.ok) {
      return false;
    }

    return true;
  } catch (error) {
    console.error(error);
  }

  return false;
}

/**
 *
 * @param notificationLocations - The notification locations to update.
 * @returns The updated user profile.
 */
export async function updateUserProfile(user: FranchiseUser): Promise<boolean> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return false;
  }

  try {
    const userId = user.userId;
    const franchiseId = user.franchiseId;
    const role = user.userRole;
    const email = user.email;
    const locations = Array.from(user.locationPermissions.entries()).map(
      ([locationId, customPermissionsMap]) => {
        // Convert the customPermissions map into a plain object
        const customPermissions = Object.fromEntries(
          customPermissionsMap.entries()
        );

        return { locationId, customPermissions, franchiseId };
      }
    );

    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/v2/user/profile`,
      {
        method: 'PUT',
        headers: {
          Authorization: token,
        },
        body: JSON.stringify({
          locations,
          userId,
          franchiseId,
          role,
          email,
        }),
      }
    );

    if (!response.ok) {
      return false;
    }

    return true;
  } catch (error) {
    console.error(error);
  }

  return false;
}

/**
 *
 * @param notificationLocations - The notification locations to update.
 * @returns The updated user profile.
 */
export async function deleteUserProfile(user: FranchiseUser): Promise<boolean> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return false;
  }

  try {
    const franchiseId = user.franchiseId;
    const userId = user.userId;

    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/user/profile`,
      {
        method: 'DELETE',
        headers: {
          Authorization: token,
        },
        body: JSON.stringify({
          userId,
          franchiseId,
        }),
      }
    );

    if (!response.ok) {
      return false;
    }

    return true;
  } catch (error) {
    console.error(error);
  }

  return false;
}

/**
 * Create a location group.
 * @param groupName - Name of the new location group.
 * @param franchiseId - Franchise of the group.
 * @param locationIds - The location IDs of the group.
 * @param isPrivate - boolean private/public group
 * @returns Name of the newly created group.
 */
export async function createGroup(
  groupName: string,
  franchiseId: string,
  locationIds: string[],
  isPrivate: boolean
): Promise<string> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return '';
  }

  const queryParams = new URLSearchParams();
  queryParams.append('user', isPrivate.toString());

  let queryString = `franchises/${franchiseId}/groups?${queryParams.toString()}`;

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/${queryString}`,
      {
        method: 'POST',
        headers: {
          Authorization: token,
        },
        body: JSON.stringify({
          groupName,
          locationIds,
        }),
      }
    );

    if (!response.ok) {
      return '';
    }

    if (response.ok) {
      const data = await response.json();
      return data as string;
    }
  } catch (error) {
    console.error(error);
  }

  return '';
}

/**
 * Delete a location group.
 * @param groupName - Name of the new location group.
 * @param franchiseId - Franchise of the group.
 * @returns Name of the newly created group.
 */
export async function deleteGroup(
  groupName: string,
  franchiseId: string,
  isPrivate: boolean,
): Promise<string> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return '';
  }

  const queryParams = new URLSearchParams();
  queryParams.append('groupId', groupName);
  queryParams.append('user', isPrivate.toString());
  let queryString = `franchises/${franchiseId}/groups?${queryParams.toString()}`;

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/${queryString}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: token,
        },
      }
    );

    if (!response.ok) {
      return '';
    }

    if (response.ok) {
      const data = await response.json();
      return data as string;
    }
  } catch (error) {
    console.error(error);
  }

  return '';
}

/**
 * Query a location group by name.
 * @param group - The LocationGroup, including name and user owned.
 * @param franchiseId - The franchise ID of the group.
 * @returns List of all location IDs in the group.
 */
export async function getGroupLocationsByName(
  group: GroupResponse,
  franchiseId: string
): Promise<string[]> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return [];
  }

  const queryParams = new URLSearchParams();
  queryParams.append('groupName', group.groupId);
  queryParams.append('user', group.private.toString());

  let queryString = `franchises/${franchiseId}/groups?${queryParams.toString()}`;

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/${queryString}`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
      }
    );

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return data as string[];
  } catch (error) {
    console.error(error);
  }

  return [];
}

/**
 * Query all location groups for a single user.
 * @param franchiseId - The franchise ID that the groups belong to.
 * @returns List of all user's location group names / IDs.
 */
export async function getAllGroups(
  franchiseId: string
): Promise<GroupResponse[]> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return [];
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/franchises/${franchiseId}/groups`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
      }
    );

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return data as GroupResponse[];
  } catch (error) {
    console.error(error);
  }

  return [];
}

export async function getUserDevices(): Promise<string[]> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return [];
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/user/device`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
      }
    );

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return data as string[];
  } catch (error) {
    console.error(error);
  }

  return [];
}

export async function getUserPreference(
  locationId?: string,
): Promise<UserPreference[]> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return [];
  }

  try {
    const queryParams = new URLSearchParams();

    // Append the location ID to the query params.
    if (locationId) {
      queryParams.append('locationId', locationId);
    }

    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/user/notificationPreferences?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
      }
    );

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return data as UserPreference[];
  } catch (error) {
    console.error(error);
  }

  return [];
}

export async function postUserPreference(
  userPrefRequest: UserPreferenceRequest,
): Promise<string> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return 'No token found';
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/user/notificationPreferences`,
      {
        method: 'POST',
        headers: {
          Authorization: token,
        },
        body: JSON.stringify({
          locationIds: userPrefRequest.locationIds,
          notificationType: userPrefRequest.notificationType,
        }),
      }
    );

    if (!response.ok) {
      return response.statusText;
    }

    const data = await response.json();
    return data.message;
  } catch (error) {
    console.error(error);
  }

  return '';
}

export async function deleteUserPreference(
  userPrefRequest: UserPreferenceRequest
): Promise<string> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return 'No token found';
  }

  try {
    const queryParams = new URLSearchParams();
    userPrefRequest.locationIds.forEach(locationId => {
      queryParams.append('locationIds', locationId);
    });
    queryParams.append('notificationType', userPrefRequest.notificationType);

    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/user/notificationPreferences?${queryParams.toString()}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: token,
        },
      }
    );

    if (!response.ok) {
      return response.statusText;
    }

    const res = await response.json();
    return res.message;
  } catch (error) {
    console.error(error);
  }

  return '';
}

// rivate String franchiseId;
//     private List<String> locationIds;
//     private String startDate;
//     private String endDate;
//     private String detectionType;

export async function getManualReport(
  franchiseId: string,
  locationIds: string[],
  startDate: string,
  endDate: string,
  toggled: string,
  reportType: string,
  timeSpan: string,
  splitBy: string[],
  measures: string[],
  groupIds: string[],
  tiles: string[],
  filters: Record<string, string[]>
) {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return null;
  }
  const detectionType = toggled;

  const response = await fetch(
    `${import.meta.env.VITE_API_URL}/franchises/${franchiseId}/export-report`,
    {
      method: 'POST',
      headers: {
        Authorization: token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        locationIds,
        startDate,
        endDate,
        detectionType,
        reportType,
        timeSpan,
        splitBy,
        measures,
        groupIds,
        tiles,
        filters
      }),
    }
  );

  if (!response.ok) {
    const errorText = await response.text(); // Read the error message
    console.error('Failed to download report:', errorText);
    throw new Error(`Failed to download report: ${errorText}`);
  }
}

export async function getAllReportConfigs(
  franchiseId: string
): Promise<ReportConfig[]> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return [];
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/franchises/${franchiseId}/report-config`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text(); // Read the error message
      console.error('Failed to retrieve report configs:', errorText);
      return [];
    }

    const data = await response.json();
    return data as ReportConfig[];
  } catch (error) {
    console.error('Error retrieving reports:', error);
    return [];
  }
}

export async function saveNewReport(
  franchiseId: string,
  displayName: string,
  locationIds: string[],
  detectionType: string,
  timeSpan: string,
  reportType: string,
  startDate: string,
  endDate: string,
  measures: string[],
  splitBys: string[],
  groupIds: string[],
  tiles: string[],
  filters: Record<string, string[]>
) {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return null;
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/franchises/${franchiseId}/report-config`,
      {
        method: 'POST',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          displayName,
          locationIds,
          detectionType,
          timeSpan,
          reportType,
          startDate,
          endDate,
          measures,
          splitBys,
          groupIds,
          tiles,
          filters
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text(); // Read the error message
      console.error('Failed to download report:', errorText);
      return false;
    }

    const data = await response.json();
    console.log(data);
    return data as ReportConfig;
  } catch (error) {
    console.error('Error saving new report:', error);
  }
}

export async function updateReport(
  reportId: string,
  franchiseId: string,
  displayName: string,
  locationIds: string[],
  detectionType: string,
  timeSpan: string,
  reportType: string,
  startDate: string,
  endDate: string,
  measures: string[],
  splitBys: string[],
  groupIds: string[],
  tiles: string[],
  filters: Record<string, string[]>
) {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return null;
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/franchises/${franchiseId}/report-config`,
      {
        method: 'PUT',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reportId,
          displayName,
          locationIds,
          detectionType,
          timeSpan,
          reportType,
          startDate,
          endDate,
          measures,
          splitBys,
          groupIds,
          tiles,
          filters
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text(); // Read the error message
      console.error('Failed to download report:', errorText);
      return false;
    }

    const data = await response.json();
    console.log(data);
  } catch (error) {
    console.error('Error updating report:', error);
  }
}
export async function deleteReportConfig(
  reportId: string,
  franchiseId: string,
) {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return null;
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/franchises/${franchiseId}/report-config`,
      {
        method: 'DELETE',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reportId,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text(); // Read the error message
      console.error('Failed to delete report:', errorText);
      return false;
    }

    const data = await response.json();
    console.log(data);
  } catch (error) {
    console.error('Error deleting report:', error);
  }
}

export async function getAllReportScheduleConfigs(
  franchiseId: string
): Promise<ReportScheduleConfig[]> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return [];
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/franchises/${franchiseId}/report-schedule-config`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text(); // Read the error message
      console.error('Failed to retrieve report configs:', errorText);
      return [];
    }

    const data = await response.json();
    return data as ReportScheduleConfig[];
  } catch (error) {
    console.error('Error retrieving reports:', error);
    return [];
  }
}

export async function saveNewReportScheduleConfig(
  franchiseId: string,
  displayName: string,
  reportId: string,
  executionMode: string,
  taskFrequency: string,
  taskFirstTime: string,
  emails: string[]

) {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return null;
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/franchises/${franchiseId}/report-schedule-config`,
      {
        method: 'POST',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          displayName,
          reportId,
          executionMode,
          taskFrequency,
          taskFirstTime,
          emails
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text(); // Read the error message
      console.error('Failed to create new Schedule', errorText);
      return false;
    }

    const data = await response.json();
    console.log(data);
    return data as ReportScheduleConfig;
  } catch (error) {
    console.error('Failed to create new Schedule', error);
  }
}

export async function updateReportScheduleConfig(
  franchiseId: string,
  displayName: string,
  reportScheduleId: string,
  reportId: string,
  executionMode: string,
  taskFrequency: string,
  taskFirstTime: string,
  emails: string[],
  active: boolean

) {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return null;
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/franchises/${franchiseId}/report-schedule-config`,
      {
        method: 'PUT',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          displayName,
          reportId,
          reportScheduleId,
          executionMode,
          taskFrequency,
          taskFirstTime,
          emails,
          active
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text(); // Read the error message
      console.error('Failed to create new Schedule', errorText);
      return false;
    }

    const data = await response.json();
    console.log(data);
    return data as ReportScheduleConfig;
  } catch (error) {
    console.error('Failed to create new Schedule', error);
  }
}

export async function deleteReportScheduleConfig(
  reportScheduleId: string,
  franchiseId: string,
) {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return null;
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/franchises/${franchiseId}/report-schedule-config`,
      {
        method: 'DELETE',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reportScheduleId,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text(); // Read the error message
      console.error('Failed to delete schedule:', errorText);
      return false;
    }

    const data = await response.json();
    console.log(data);
  } catch (error) {
    console.error('Error deleting schedule:', error);
  }
}

export async function getReportArtifactById(
  franchiseId: string,
  artifactId: string,
) {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return null;
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/franchises/${franchiseId}/report-schedule-config/artifacts/${artifactId}`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        }
      }
    );

    if (!response.ok) {
      const errorText = await response.text(); // Read the error message
      console.error('Failed to create new Schedule', errorText);
      return false;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Failed to create new Schedule', error);
  }
}

export async function getReportArtifactList(
  franchiseId: string,
) {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return null;
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/franchises/${franchiseId}/report-schedule-config/artifacts`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        }
      }
    );

    if (!response.ok) {
      const errorText = await response.text(); // Read the error message
      console.error('Failed to create new Schedule', errorText);
      return false;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Failed to create new Schedule', error);
  }
}

export async function getLocationStatus(
  franchiseId: string,
): Promise<LocationStatus[]> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return [];
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/franchises/${franchiseId}/locations/statuses`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
      }
    );

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return data as LocationStatus[];
  } catch (error) {
    console.error(error);
  }

  return [];
}

export async function putLocationStatuses(
  franchiseId: string,
  locationId: string,
  updates: Map<string, StatusRequestObject>
): Promise<boolean> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return false;
  }

  try {
    const response = await fetch(
      `${
        import.meta.env.VITE_API_URL
      }/franchises/${franchiseId}/locations/${locationId}/status`,
      {
        method: 'PUT',
        headers: {
          Authorization: token,
        },
        body: JSON.stringify({
          updates: [...Object.values(updates)]
        }),
      }
    );

    if (!response.ok) {
      return false;
    }

    return true;
  } catch (error) {
    console.error(error);
  }

  return false;
}

export async function getFranchiseReportConfigs(
  franchiseId: string,
): Promise<ReportScheduleConfig | undefined> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return undefined;
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/franchises/${franchiseId}/reports`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        }
      }
    );

    if (!response.ok) {
      const errorText = await response.text(); // Read the error message
      console.error('Failed to get report configs', errorText);
      return undefined;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Failed to get report configs', error);
  }
}

export async function updateFranchiseReportConfigs(
  franchiseId: string,
  franchiseReportConfigs: ReportScheduleConfig[]
): Promise<ReportScheduleConfig | undefined> {
  const token = await getUserToken();

  if (token === null) {
    console.log('No token found.');
    return undefined;
  }

  const reportScheduleUpdates = {
    reportScheduleUpdates: franchiseReportConfigs.map((config) => ({
      displayName: config.displayName,
      reportId: config.reportId,
      reportScheduleId: config.reportScheduleId,
      executionMode: config.executionMode,
      taskFrequency: config.taskFrequency,
      taskFirstTime: config.taskFirstTime,
      emails: config.emails,
      active: config.active,
    })),
  };

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/franchises/${franchiseId}/reports`,
      {
        method: 'POST',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(
          reportScheduleUpdates
        )
      }
    );

    if (!response.ok) {
      const errorText = await response.text(); // Read the error message
      console.error('Failed to get report configs', errorText);
      return undefined;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Failed to get report configs', error);
  }
}
